import { Component, Inject, OnInit, Optional, Input, Output, EventEmitter } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { DocumentPickerComponent } from '../document-picker/document-picker.component';
import { HttpEventType } from '@angular/common/http';
import { Observable, Subscription } from 'rxjs';
import { DocumentService } from '../../document/document.service';
import { AttachmentsServiceService } from '../../../services/attachments/attachments-service.service';
import { ToasterService } from '../../../services/core/toaster.service';
import { ThumbnailGeneratorService } from '../../../services/attachments/thumbnail-generator.service';

interface UploadFile {
  file: File;
  preview: string | null;
  progress: number;
  status: 'pending' | 'uploading' | 'success' | 'error';
  error?: string;
  // Store API response data
  responseData?: any;
  attach_file?: any;
  attachment_id?: number;
}

@Component({
    selector: 'app-media-upload-dialog',
    templateUrl: './media-upload-dialog.component.html',
    styleUrls: ['./media-upload-dialog.component.scss'],
    standalone: false
})
export class MediaUploadDialogComponent implements OnInit {
  // Input properties for inline usage
  @Input() inline: boolean = false;
  @Input() moduleId!: number;
  @Input() recordId: number | undefined;
  @Input() folderId: number | string = '';
  @Input() uploadType: 'media' | 'document' = 'document';
  @Input() endpoint?: string;
  @Input() accept?: string;
  @Input() allowedExtensions?: string[];
  /**
   * Show the "Additional files" toggle. Turning it on REPLACES the drop zone with one that can
   * also take a file already filed in ProSuite, picked from the document manager.
   *
   * Opt-in: this dialog is shared by the gallery, documents, bid drawings and the vendor-quote
   * upload, and only the last has a file that is routinely already in the system — a vendor
   * emails the quote, it is filed against the job, and uploading it again is busywork.
   */
  @Input() allowSystemFileSource = false;

  /** Which box is showing. Only one is on screen, so there is no question where the file goes. */
  fileSource: 'local' | 'prosuite' = 'local';

  /**
   * The ProSuite file standing in for the upload: `{ id, name, type, folder, document }`, where
   * `id` is a `documents` row. One file — it IS the document this dialog is here for, not an extra.
   */
  pickedDocument: { id: number; name: string; type: string; folder: string; document: any } | null = null;
  @Input() dialogTitleOverride?: string;
  /** Overrides the "Supported: …" line under the drop zone. */
  @Input() supportedFilesTextOverride?: string;
  /** Keep at most this many files; a new pick replaces the oldest. */
  @Input() maxFiles?: number;

  /**
   * Reading mode (Upload Vendor Quote): after Upload All the dialog stays open and shows a
   * step checklist while `startReading` runs, then closes with `{ success, reading: <last event> }`.
   * Dropping a file starts it straight away. Off unless `readingStages` + `startReading` are given.
   */
  readingStages?: Array<{ stage: string; label: string }>;
  /**
   * Receives the uploaded file and the attach_files id the upload produced, or — when the file was
   * picked from ProSuite instead of uploaded — no file at all and the `documents` id to read.
   * Exactly one of the two ids is ever set.
   */
  startReading?: (file: File | null, attachFileId: number | null, documentId?: number | null)
    => Observable<{ stage: string; [key: string]: any }>;
  readingFooterText = 'Reading the quote…';
  /**
   * Shown under the checklist while the read runs. Opt-in: callers that finish quickly pass
   * nothing and get no hint. Set it where the wait is long enough that silence reads as a hang.
   */
  readingHintText = '';
  isReading = false;
  readingIndex = -1;
  private readingSub?: Subscription;

  get isReadingMode(): boolean {
    return !!(this.readingStages?.length && this.startReading);
  }

  // Output events for inline usage
  @Output() filesUploaded = new EventEmitter<number[]>();
  @Output() uploadComplete = new EventEmitter<{ success: boolean; files: UploadFile[] }>();

  files: UploadFile[] = [];
  isDragging = false;
  isUploading = false;

  // Stats
  totalFiles = 0;
  uploadedFiles = 0;
  failedFiles = 0;

  // Check if component is used as dialog or inline
  get isDialogMode(): boolean {
    return !this.inline && !!this.dialogRef;
  }

  // Dynamic text based on upload type
  get dialogTitle(): string {
    if (this.dialogTitleOverride) {
      return this.dialogTitleOverride;
    }
    return this.uploadType === 'media'
      ? 'Upload Media Files'
      : 'Upload Document Files';
  }

  get supportedFilesText(): string {
    if (this.supportedFilesTextOverride) {
      return this.supportedFilesTextOverride;
    }
    return this.uploadType === 'media'
      ? 'Supported: Images (JPG, PNG, GIF) and Videos (MP4, WebM)'
      : 'Supported: PDF, DOC, DOCX, XLS, XLSX, TXT, and more';
  }

  get acceptedFileTypes(): string {
    if (this.accept) {
      return this.accept;
    }
    return this.uploadType === 'media' ? 'image/*,video/*' : '*';
  }

  constructor(
    @Optional() public dialogRef: MatDialogRef<MediaUploadDialogComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any,
    private documentService: DocumentService,
    private attachmentService: AttachmentsServiceService,
    private toasterService: ToasterService,
    private thumbnailGeneratorService: ThumbnailGeneratorService,
    @Optional() private dialog: MatDialog
  ) {
    // Initialize from dialog data if available (dialog mode)
    if (data) {
      this.moduleId = data.module_id || 19;
      this.recordId = data.recordId || data.scope_id || data.attachable_id;
      // Keep empty string when no folder (root). Do NOT coerce to 0.
      this.folderId =
        data.folder_id === 0 || data.folder_id === '0' ? 0 : data.folder_id ?? '';
      this.uploadType = data.type || 'document';
      this.endpoint = data.endpoint;
      this.accept = data.accept;
      this.allowedExtensions = data.allowedExtensions;
      this.allowSystemFileSource = !!data.allowSystemFileSource;
      this.dialogTitleOverride = data.title;
      this.supportedFilesTextOverride = data.supportedFilesText;
      this.maxFiles = data.maxFiles;
      this.readingStages = data.readingStages;
      this.startReading = data.startReading;
      if (data.readingFooterText) {
        this.readingFooterText = data.readingFooterText;
      }
      if (data.readingHintText) {
        this.readingHintText = data.readingHintText;
      }

      // If files were passed (from drag-drop), add them
      if (data.files && data.files.length > 0) {
        this.addFiles(data.files);
      }
    }
    // Otherwise, properties will be set via @Input bindings (inline mode)
  }

  ngOnInit(): void {
    // Initialize component
  }

  onFileSelect(event: any): void {
    const files = event.target.files;
    if (files && files.length > 0) {
      this.addFiles(files);
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  // ── Additional files (opt-in via allowSystemFileSource) ─────────────────────
  // Two ways in, one file: dropped or browsed from the computer, or picked from what is already
  // filed in ProSuite. Choosing one drops the other, so the dialog never holds two candidates for
  // the same document.

  /** True when the dialog is holding something to work with, wherever it came from. */
  get hasChosenFile(): boolean {
    return this.files.length > 0 || !!this.pickedDocument;
  }

  /** The picked ProSuite file's list icon — pictures and Word files are not all PDFs. */
  get pickedDocumentIcon(): string {
    if (this.isImageFile(this.pickedDocument)) {
      return 'image';
    }

    return this.isWordFile(this.pickedDocument) ? 'description' : 'picture_as_pdf';
  }

  /**
   * Switch source. Nothing is discarded here — a toggle pressed by mistake must not lose the file
   * the user already chose; it is only replaced when they actually choose in the other source.
   */
  onToggleFileSource(useProSuite: boolean): void {
    if (this.isUploading || this.isReading) {
      return;
    }
    this.fileSource = useProSuite ? 'prosuite' : 'local';
  }

  /**
   * Pick the file from what is already filed in ProSuite, instead of uploading it again.
   *
   * One file only: this IS the document the dialog is here for. In reading mode the read starts
   * straight away, exactly as dropping a file does.
   */
  openDocumentManager(): void {
    if (!this.dialog || this.isUploading || this.isReading) {
      return;
    }
    const ref = this.dialog.open(DocumentPickerComponent, {
      width: '860px',
      maxWidth: '95vw',
      panelClass: 'document-picker-dialog',
      autoFocus: false,
      data: {
        selectable: 'file',
        multiple: false,
        accept: this.allowedExtensions,
        allowRecent: true,
        // Where the picker opens; falls back to the record this dialog already files against.
        moduleId: this.data?.browseModuleId ?? this.moduleId,
        referenceId: this.data?.browseReferenceId ?? this.recordId,
        folderDisplayName: this.data?.browseFolderName,
      },
    });
    ref.afterClosed().subscribe((result: any) => {
      const doc = result?.file ?? (result?.files ?? [])[0];
      if (!(Number(doc?.id) > 0)) {
        return;
      }
      this.pickedDocument = {
        id: Number(doc.id),
        name: doc.original_name || doc.file_name || 'Untitled',
        type: doc.file_type || '',
        folder: result?.folder?.name || '',
        document: doc,
      };
      // One file: what was chosen from the computer is no longer a candidate.
      this.files = [];
      this.totalFiles = 0;
      if (this.isReadingMode && !this.isUploading && !this.isReading) {
        this.uploadFiles();
      }
    });
  }

  removePickedDocument(): void {
    this.pickedDocument = null;
  }

  /** Word files get a document icon; pictures get an image one. */
  isWordFile(file: any): boolean {
    return /\.docx?$/i.test(file?.name ?? '');
  }

  isImageFile(file: any): boolean {
    return /\.(jpe?g|png|gif|webp)$/i.test(file?.name ?? '');
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.addFiles(files);
      // Reading mode: dropping a file starts the same flow as Upload All.
      if (this.isReadingMode && this.files.length > 0 && !this.isUploading && !this.isReading) {
        this.uploadFiles();
      }
    }
  }

  private addFiles(fileList: FileList): void {
    for (let i = 0; i < fileList.length; i++) {
      const file = fileList[i];

      // Validate file type based on upload type
      if (this.allowedExtensions && !this.isAllowedByExtension(file)) {
        this.toasterService.showError(
          `${file.name} is not an allowed file type`
        );
        continue;
      }
      if (
        !this.allowedExtensions &&
        this.uploadType === 'media' &&
        !this.isMediaFile(file)
      ) {
        this.toasterService.showError(
          `${file.name} is not a supported media file`
        );
        continue;
      }

      const uploadFile: UploadFile = {
        file: file,
        preview: null,
        progress: 0,
        status: 'pending',
      };

      // Generate preview (images, videos, PDFs) via the shared thumbnail service
      this.generatePreview(uploadFile);

      this.files.push(uploadFile);
      if (this.maxFiles && this.files.length > this.maxFiles) {
        this.files.splice(0, this.files.length - this.maxFiles);
      }
    }

    this.totalFiles = this.files.length;
    // One file: choosing from the computer drops what was picked from ProSuite.
    if (this.files.length > 0) {
      this.pickedDocument = null;
    }
  }

  private isMediaFile(file: File): boolean {
    const imageTypes = [
      'image/jpeg',
      'image/png',
      'image/gif',
      'image/webp',
      'image/svg+xml',
    ];
    const videoTypes = [
      'video/mp4',
      'video/webm',
      'video/ogg',
      'video/quicktime',
    ];
    return imageTypes.includes(file.type) || videoTypes.includes(file.type);
  }

  private isDocumentFile(file: File): boolean {
    // For document type, accept all files (validation can be server-side)
    return true;
  }

  private isAllowedByExtension(file: File): boolean {
    if (!this.allowedExtensions || this.allowedExtensions.length === 0) {
      return true;
    }
    const name = file.name.toLowerCase();
    return this.allowedExtensions.some((ext) =>
      name.endsWith(ext.toLowerCase())
    );
  }

  private generatePreview(uploadFile: UploadFile): void {
    this.thumbnailGeneratorService.generateForFile(uploadFile.file).then((preview) => {
      uploadFile.preview = preview;
    });
  }

  removeFile(index: number): void {
    this.files.splice(index, 1);
    this.totalFiles = this.files.length;
  }

  async uploadFiles(): Promise<void> {
    // A file picked from ProSuite is already on the server, so there is nothing to upload — it
    // goes straight to the read.
    if (this.pickedDocument && this.isReadingMode) {
      this.readPickedDocument();

      return;
    }
    if (this.files.length === 0) {
      this.toasterService.showError('No files to upload');
      return;
    }
    if (this.isReadingMode) {
      this.uploadAndRead();
      return;
    }

    this.isUploading = true;
    this.uploadedFiles = 0;
    this.failedFiles = 0;

    for (const uploadFile of this.files) {
      if (uploadFile.status === 'success') {
        continue; // Skip already uploaded files
      }

      uploadFile.status = 'uploading';

      try {
        await this.uploadSingleFile(uploadFile);
        uploadFile.status = 'success';
        uploadFile.progress = 100;
        this.uploadedFiles++;
      } catch (error: any) {
        uploadFile.status = 'error';
        uploadFile.error = error.message || 'Upload failed';
        this.failedFiles++;
      }
    }

    this.isUploading = false;

    if (this.failedFiles === 0) {
      const successFiles = this.files.filter((f) => f.status === 'success');

      // Emit events for inline mode
      if (!this.isDialogMode) {
        const uploadedFileIds = successFiles
          .map((f) => f.responseData?.attachment_id || f.attachment_id)
          .filter(id => id) as number[];

        this.filesUploaded.emit(uploadedFileIds);
        this.uploadComplete.emit({
          success: true,
          files: successFiles,
        });

        this.toasterService.showSuccess(`${this.uploadedFiles} file(s) uploaded successfully`);

        // Clear files after successful upload in inline mode
        this.files = [];
        this.totalFiles = 0;
      }
      // Close dialog in dialog mode
      else {
        this.dialogRef.close({
          success: true,
          data: {
            uploaded: this.uploadedFiles,
            files: successFiles,
          },
        });
      }
    } else {
      if (!this.isDialogMode) {
        this.toasterService.showWarning(`${this.uploadedFiles} uploaded, ${this.failedFiles} failed`);
      }
    }
  }

  /**
   * Reading mode: upload the file, then run `startReading` on it and tick the checklist.
   *
   * The upload is real — the reader runs server-side and downloads the document by URL, so it
   * cannot work from a browser blob. `attachment_id` from the upload response is handed to
   * `startReading`, which is what the extraction endpoint keys off.
   *
   * A failed upload never reaches the reader: there would be nothing to read, and the checklist
   * would stall on a step that could not finish.
   */
  private async uploadAndRead(): Promise<void> {
    const target = this.files[0];
    this.isUploading = true;
    target.status = 'uploading';

    try {
      await this.uploadSingleFile(target);
    } catch {
      this.isUploading = false;
      target.status = 'error';
      target.error = 'We could not upload this file';
      this.toasterService.showError('We could not upload this file. Try again.');
      return;
    }

    this.isUploading = false;
    target.status = 'success';
    target.progress = 100;
    this.uploadedFiles = 1;

    this.runRead(target.file, target.attachment_id ?? null, null, target);
  }

  /**
   * Reading mode, ProSuite source: the file is already on the server, so there is nothing to
   * upload — the `documents` id goes to the reader, which resolves the stored file from it.
   */
  private readPickedDocument(): void {
    this.runRead(null, null, this.pickedDocument!.id, null);
  }

  /**
   * Runs the read and ticks the checklist, whichever source the document came from. Exactly one of
   * `attachFileId` / `documentId` is set; `target` is the upload row to mark on failure, if any.
   */
  private runRead(file: File | null, attachFileId: number | null, documentId: number | null,
                  target: UploadFile | null): void {
    this.isReading = true;
    this.readingIndex = 0;

    this.readingSub = this.startReading!(file, attachFileId, documentId).subscribe({
      next: (event) => {
        const i = this.readingStages!.findIndex((s) => s.stage === event.stage);
        if (i >= 0) {
          this.readingIndex = i;
          return;
        }
        if (event.stage === 'ready') {
          this.readingIndex = this.readingStages!.length;
          this.isReading = false;
          this.dialogRef?.close({
            success: true,
            reading: event,
            file: file ?? undefined,
            document: this.pickedDocument?.document,
          });
        }
      },
      // The reader reports WHY it stopped (no document attached, nothing recognised in it, the
      // service unreachable). Showing a fixed sentence instead threw that away and made every
      // cause look identical — which is exactly how a misconfigured service URL gets mistaken for
      // an unreadable document. Fall back to the generic wording only when there is no reason.
      error: (err: any) => {
        this.isReading = false;
        this.readingIndex = -1;
        const reason =
          (typeof err?.message === 'string' && err.message) ||
          (typeof err?.error?.message === 'string' && err.error.message) ||
          '';
        if (target) {
          target.status = 'error';
          target.error = reason || 'We could not read this file';
        }
        this.toasterService.showError(
          reason
            ? `${reason} You can still add the quote manually.`
            : 'We could not read this file. Try again or add the quote manually.',
        );
      },
    });
  }

  readingStepState(index: number): 'done' | 'run' | '' {
    if (index < this.readingIndex) return 'done';
    return index === this.readingIndex ? 'run' : '';
  }

  private uploadSingleFile(uploadFile: UploadFile): Promise<void> {
    return new Promise((resolve, reject) => {
      // Prepare upload parameters
      const uploadParams = {
        module_id: this.moduleId,
        folder_id: (this.folderId === '' ? '' : Number(this.folderId)) as any,
        attachable_id: this.recordId,
        attachment_field: 'image',
      };

      // Upload using custom endpoint if provided, else default document endpoint
      const upload$ = this.endpoint
        ? this.documentService.uploadToEndpointWithProgress(
            this.endpoint,
            uploadFile.file,
            uploadParams
          )
        : this.documentService.uploadFileWithProgress(
            uploadFile.file,
            uploadParams as any
          );

      upload$.subscribe(
        (event: any) => {
          // Handle different HTTP event types
          if (event.type === HttpEventType.UploadProgress) {
            // Calculate real upload progress
            if (event.total) {
              const percentDone = Math.round(
                (100 * event.loaded) / event.total
              );
              uploadFile.progress = percentDone;
            }
          } else if (event.type === HttpEventType.Response) {
            // Upload complete - Store response data
            uploadFile.progress = 100;

            // Store the API response body
            const responseBody = event.body;
            console.log('Upload response:', responseBody);

            // Store response data in uploadFile
            uploadFile.responseData = responseBody;

            // Extract attachment data from the response.
            //
            // Two shapes are in play. The document endpoint nests everything under `data`, while
            // POST /attachments (AttachmentController::storeService) returns `attachment_id` and
            // `attach_file` at the TOP level. Reading only `data` left attachment_id undefined for
            // the second, which silently broke any caller that needs the id — the vendor-quote
            // reader among them, since it has nothing to read without a stored file.
            if (responseBody) {
              const payload = responseBody.data ?? responseBody;
              uploadFile.attach_file = payload.attach_file || payload;
              uploadFile.attachment_id =
                payload.attachment_id ?? payload.attach_file?.id ?? payload.id ?? null;
            }

            resolve();
          }
        },
        (error: any) => {
          console.error('Upload error:', error);
          reject(error);
        }
      );
    });
  }

  getFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }

  getFileIcon(file: File): string {
    if (file.type.startsWith('image/')) {
      return 'image';
    } else if (file.type.startsWith('video/')) {
      return 'videocam';
    }
    return 'insert_drive_file';
  }

  cancel(): void {
    if (this.isReading) {
      this.readingSub?.unsubscribe();
      this.isReading = false;
      this.dialogRef?.close({ success: false, cancelled: true });
      return;
    }
    if (this.isUploading) {
      const confirm = window.confirm(
        'Upload in progress. Are you sure you want to cancel?'
      );
      if (!confirm) {
        return;
      }
    }

    // Only close dialog in dialog mode
    if (this.isDialogMode) {
      this.dialogRef.close();
    }
    // In inline mode, just clear files
    else {
      this.files = [];
      this.totalFiles = 0;
    }
  }
}
